import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import ReactECharts from 'echarts-for-react';

// Constants
import {
  // DATA_RETRIEVAL_INTERVAL,
  INTERVAL_UPDATE_DATA,
  PAGE_TITLE,
  PAGE_TITLE_COMPONENT,
  TEXT_LENGTH_LIMITS,
} from '../../../../../constants/common';

// Store
import { Dispatch, RootState } from '../../../../../store/store';

// Types
import { ElementSize } from '../../../../../types/element-size';
import { ButtonType } from '../../../../../types/button-type';

// Enums
import CycleMeasurement from '../../../../../enums/cycle-measurement';
import { Action } from '../../../../../enums/action';

// Utils
import fahrenheitToCelsius from '../../../../../utils/fahrenheitToCelsius';

// Components
import Modal from '../../../../Modal/Modal';
import InteractiveButton from '../../../../InteractiveButton/InteractiveButton';
import SpinnerLoader from '../../../../SpinnerLoader/SpinnerLoader';
import TextAreaControl from '../../../../TextAreaControl/TextAreaControl';
import SelectOption, { Option, SelectedValue } from '../../../../SelectOption/SelectOption';
import { AbilityContext } from '../../../../Can/Can';
import IconGear from '../../../../Icons/IconGear/IconGear';
import IconMultipleLines from '../../../../Icons/MultipleLines/MultipleLines';
import GraphScreenshot from '../../../../GraphScreenshot/GraphScreenshot';
import ChartPreferences from '../../../../ChartPreferences/ChartPreferences';
import { CycleComponents, DataZoomEvent, DeviceColor, NoteSeriesDataType } from '../../../../CycleChartPreview/types';
import { useTitle } from '../../../../../hooks/useTitle';
import { generateControllersName, generateCycleData, getNumberNoteSeries, sortNoteSeries } from '../../../utils';
import { CycleController } from '../../../../../store/models/controllers';
import getDeviceNamesUtil from '../../../../../utils/getDeviceNames';
import getTempByMeasurement from '../../../../../utils/getTempByMeasurement';
import getMeasurementPreference from '../../../../../utils/getMeasurementPreference';
import { ChartPreference } from '../../../../../store/models/chart-preference';
import applyYAxisPadding from '../../../../../utils/applyYAxisPadding';
import ReportChannelsModal, { IColorsDevices, IExcludedChannelNames } from '../../../../ReportChannelsModal/ReportChannelsModal';
import { Note, NoteLink } from '../../../types';
import { ICustomChannelName } from '../../../../../types/channel-types';
import { SeriesOption } from 'echarts';
import { CHARACTER_SEPARATOR_IOT_KEY_NAME, NOTE_ID, SET_POINT_NAME } from '../../../../CycleChartPreview/constants';
import Tooltip from '../../../../Tooltip';
import { useTranslation } from 'react-i18next';
import { IoTDataResponse } from '../../../../../store/models/cycles';
import { StaticChart } from '../../../../CycleChartPreview/StaticChart';

type DispatchProps = ReturnType<typeof mapDispatch>
type StateProps = ReturnType<typeof mapState>;

type ComponentProps = DispatchProps & StateProps

type NoeFormValues = {
  id: string | null;
  name: string | null;
  coordinate: { x: number | null; y: number | null };
};

const CycleChart: React.FC<ComponentProps> = ({
  cycleControllers,
  cycleIotData,
  cycleNotes,
  currentUserMeasurement,
  getCycle,
  deleteNote,
  createNote,
  updateNote,
  createNoteLink,
  deleteNoteLink,
  currentUserMember,
  updateViewNotes,
  chartPreference,
  getChartPreference,
  getChannels,
  getExcludeChannelNames,
  updateCycleData,
  cycle,
  clearCycle,
  clearChartPreference,
  clearNote,
  clearChannels,
  getCustomChannelNames,
  getColorDevices,
  firstLoadComplete,
  setFirstLoadComplete,
}) => {
  const getColorsDevicesByChart = (chartRef, colorsDevices, cycle): IColorsDevices[] => {
    if (!cycle) return colorsDevices;

    const chartInstance = chartRef?.current?.getEchartsInstance();
    if (!chartInstance) return colorsDevices;

    const series = chartInstance.getOption().series as SeriesOption[];

    return cycle.cycle_controllers.map((cc) => {
      const controllerSerialNumber = cc.controller.serial_number;
      const controllerId = cc.controllerId;

      const seriesMapByDevice: Record<string, string | string[]> = {};

      series.forEach((ser) => {
        const isNote = String(ser.name).startsWith(NOTE_ID);
        if (ser.id === SET_POINT_NAME || !ser.color || isNote) return;

        const [serial, device] = String(ser.id).split(CHARACTER_SEPARATOR_IOT_KEY_NAME);
        if (serial === controllerSerialNumber) {
          seriesMapByDevice[device] = ser.color as string | string[];
        }
      });

      const storedColors = colorsDevices.find(item => item.controllerId === controllerId);

      const buildColorArray = (
        deviceList: string[],
        stored: DeviceColor[],
      ): DeviceColor[] => {
        return deviceList.map((device) => {
          const storedColor = stored?.find((item) => Object.keys(item)[0] === device);
          const chartColor = seriesMapByDevice[device];
          return storedColor ?? (chartColor ? { [device]: chartColor } : { [device]: null }) as DeviceColor;
        });
      };

      const channelColors = buildColorArray(cc.channels, storedColors?.channelColors ?? []);
      const thermocoupleColors = buildColorArray(cc.thermocouples, storedColors?.thermocoupleColors ?? []);

      return {
        controllerId,
        channelColors,
        thermocoupleColors,
      };
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const { clientId, contractId, screenId, id } = useParams();
  const { t } = useTranslation();

  const setBucketQuery = (bucket: string) => {
    setSearchParams({
      ['bucket']: bucket,
    });
  };

  const setupDefaultBucket = () => {
    setBucketQuery(`${1}`);
  };

  const setActiveBucket = (urLSearchParams: URLSearchParams) => {
    const data = urLSearchParams.get('bucket');

    const size = parseInt(data)

    if (isNaN(size) || !size) {
      return setupDefaultBucket();
    }

    setBucketQuery(data);
  };

  useEffect(() => {
    setActiveBucket(searchParams);
  }, [searchParams]);

  const bucket = useMemo(() => {
    return searchParams.get('bucket')
  }, [searchParams])

  const ability = useContext(AbilityContext);
  const IS_ACCESS = {
    NOTE: {
      CREATE: ability.can(Action.Create, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
      DELETE: ability.can(Action.Delete, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
      UPDATE: ability.can(Action.Update, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
    },
    CYCLE: {
      UPDATE: ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
    },
    CHART_PREFERENCES: {
      UPDATE: ability.can(Action.Update, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].CHART_PREFERENCES),
    }
  };

  const chartRef = useRef<ReactECharts | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [lastFetchedDate, setLastFetchedDate] = useState<number>(0)
  const [isCreationNote, setIsCreationNote] = useState<boolean>(false);
  const [isEditNote, setIsEditNote] = useState<boolean>(false);
  const [isEditChartPreferences, setIsEditChartPreferences] = useState<boolean>(false);
  const [isEditReportChannels, setIsEditReportChannels] = useState<boolean>(false);
  const [isDeletionNote, setIsDeletionNote] = useState<boolean>(false);
  const [isDeletingChain, setIsDeletingChain] = useState<boolean>(false);
  const [linkTargetNoteId, setLinkTargetNoteId] = useState<string | null>(null);
  const [editingOccurrenceId, setEditingOccurrenceId] = useState<string>('root');
  const [linkingNoteId, setLinkingNoteId] = useState<string | null>(null);
  const linkingNoteIdRef = useRef<string | null>(null);
  const cycleRef = useRef(cycle);
  const isDeletingChainRef = useRef<boolean>(false);

  const [cycleComponents, setCycleComponent] = useState<CycleComponents | null>(null);
  const [iotData, setIotData] = useState(null);
  const [cycleControllersName, setCycleControllersName] = useState<{ [key: string]: string } | null>(null);
  const [dataZoom] = useState<DataZoomEvent | null>(null);
  const [cycleName, setCycleName] = useState<string>('undefined');
  const [nameDevices, setNameDevices] = useState<{ [key: string]: string } | null>(null);
  const [tempRange, setTempRange] = useState<Pick<ChartPreference, 'min_temperature' | 'max_temperature'> | null>(null);
  const [excludedChannelNames, setExcludedChannelNames] = useState<IExcludedChannelNames[] | null>(null);
  const [channelNames, setChannelNames] = useState<ICustomChannelName[] | null>(null);
  const [colorsDevices, setColorsDevices] = useState<IColorsDevices[] | null>(null);
  const [isFirstLoadComplete, setIsFirstLoadComplete] = useState<boolean>(false);

  const isStopPullingIotData = isLoading || isCreationNote || isDeletionNote || isEditChartPreferences || isEditReportChannels || !!linkingNoteId;

  useEffect(() => {
    linkingNoteIdRef.current = linkingNoteId;
  }, [linkingNoteId]);

  useEffect(() => {
    isDeletingChainRef.current = isDeletingChain;
  }, [isDeletingChain]);

  useEffect(() => {
    cycleRef.current = cycle;
  }, [cycle]);

  const isPullingIotData = useMemo(() => chartPreference ? chartPreference.end_timestamp === null : false, [chartPreference]);

  useTitle(cycleName);

  const generateNoteName = (name: string) => {
    return `${NOTE_ID}: ${name}`;
  }

  const onSubmitFormHandler = async (values: NoeFormValues) => {
    if (isCreationNote) {
      const { name, coordinate } = values;
      const formValued = { name, coordinate };

      if (currentUserMeasurement === CycleMeasurement.Fahrenheit) {
        formValued.coordinate.y = Number(fahrenheitToCelsius(formValued.coordinate.y));
      }

      if (linkTargetNoteId) {
        const response = await createNoteLink({
          noteId: linkTargetNoteId,
          payload: { coordinate: formValued.coordinate },
        });
        addLinkToListNotes(linkTargetNoteId, response);

        const updatedListNotes = cycleRef.current.notes.map((noteData) =>
          noteData.id === linkTargetNoteId
            ? { ...noteData, links: [...(noteData.links ?? []), response] }
            : noteData
        );
        updateCycleData({ notes: updatedListNotes });

        closeModalCreation();
        return;
      }

      const response = await createNote({ ...formValued, cycleId: id });
      expandListNotes(response);

      updateCycleData({ notes: [...cycleRef.current.notes, response] });

      closeModalCreation();
      return;
    }

    if (isDeletionNote) {
      const { id } = values;

      const response = await deleteNote({ id, deleteAll: isDeletingChainRef.current });

      if (response?.promoted) {
        promoteLinkInListNotes(id, response.promotedLinkId, response.coordinate);

        const updatedListNotes = cycleRef.current.notes.map((noteData) =>
          noteData.id === id
            ? {
                ...noteData,
                coordinate: response.coordinate,
                links: (noteData.links ?? []).filter((link) => link.id !== response.promotedLinkId),
              }
            : noteData
        );
        updateCycleData({ notes: updatedListNotes });

        closeModalEditing();
        return;
      }

      deleteNoteFromListNotes(id);

      const updatedListNotes = cycleRef.current.notes.filter((noteData) => noteData.id !== id);

      updateCycleData({ notes: updatedListNotes });

      closeModalEditing();

      // Confirmed (not just a stale-backend symptom): the incremental chart patch
      // above doesn't reliably remove a whole marker from the live chart — force a
      // full resync, matching what a manual refresh already does reliably.
      // NOTE: `id` here is the note's id (destructured from `values` above),
      // not the cycle's — use the cycle id via the ref to avoid that shadowing.
      await fetchData(cycleRef.current.id);
      return;
    }

    if (isEditNote) {
      const { name, id, coordinate } = values;
      const payload = { name };
      const response = await updateNote({
        id: values.id,
        payload,
      });


      const oldNoteData = { name, id, coordinate };
      updateListNotes(oldNoteData, response);

      const existingLinks = cycleRef.current.notes.find((noteData) => noteData.id === id)?.links;
      const updatedListNotes = cycleRef.current.notes.filter((noteData) => noteData.id !== id);
      updatedListNotes.push({ ...response, links: existingLinks });

      updateCycleData({ notes: updatedListNotes });


      closeModalEditing();
      return;
    }
  };

  const noteValidationSchema = useMemo(
    () =>
      object({
        name: linkTargetNoteId
          ? string().notRequired()
          : string()
              .min(TEXT_LENGTH_LIMITS.MIN, `Minimum ${TEXT_LENGTH_LIMITS.MIN} characters required`)
              .max(TEXT_LENGTH_LIMITS.MAX, `Maximum ${TEXT_LENGTH_LIMITS.MAX} characters required`)
              .required('Required'),
      }),
    [linkTargetNoteId],
  );

  const formik = useFormik<NoeFormValues>({
    initialValues: {
      id: null,
      name: '',
      coordinate: { x: null, y: null },
    },
    validationSchema: noteValidationSchema,
    onSubmit: onSubmitFormHandler,
  });

  const isDisabledNoteBtn = Boolean(formik.touched.name && formik.errors.name);

  const fetchChartSettings = async (id: string) => {
    const excluded = await getExcludeChannelNames(id);
    const colorsDevices = await getColorDevices(id);

    setExcludedChannelNames(excluded)
    setColorsDevices(colorsDevices);
  }

  const fetchData = async (id: string) => {
    const chartPreference = await getChartPreference(id);
    const from = chartPreference.start_timestamp;
    const to = chartPreference.end_timestamp === null ? new Date().getTime() : chartPreference.end_timestamp;
    const bucketSize = bucket ? parseInt(bucket) : 1

    const excluded = await getExcludeChannelNames(id);
    const colorsDevices = await getColorDevices(id);

    setIsLoading(true);
    
    clearCycle();
    
    const cycleData = await getCycle({
      id,
      query: {
        from: String(from),
        to: String(to),
        bucket: bucketSize
      },
      skipRetention: true,
      staticFirstDataLoad: true,
    });

    setLastFetchedDate(to)

    if (cycleData.name) {
      setCycleName(cycleData.name);
    }

    setExcludedChannelNames(excluded)
    setColorsDevices(colorsDevices);

    setIsLoading(false);

    const fetchChannelNames = async () => {
      const names = await getCustomChannelNames(id);
      setChannelNames(names);
    };
    fetchChannelNames();
  };

  const updateTempRange = (min: number, max: number) => {
    const convertedMinTemp = getTempByMeasurement(currentUserMeasurement, chartPreference.min_temperature);
    const convertedMaxTemp = getTempByMeasurement(currentUserMeasurement, chartPreference.max_temperature);

    const min_temperature = applyYAxisPadding(min, { min: convertedMinTemp, max: convertedMaxTemp }, 'min');
    const max_temperature = applyYAxisPadding(max, { min: convertedMinTemp, max: convertedMaxTemp }, 'max');

    setTempRange({ min_temperature, max_temperature });
  };

  const getDeviceNames = async (cycleControllers: CycleController[]) => {
    const promiseChannels = cycleControllers.map((item) => {
      return getChannels(item.controllerId);
    });

    const deviceNames = await getDeviceNamesUtil(cycleControllers, promiseChannels);
    setNameDevices(deviceNames);
  };

  const fetchNewData = async (from: number) => {
    const filterTo = new Date().getTime();

    const bucketSize = bucket ? parseInt(bucket) : 1
    await getCycle({
      id,
      query: { from: String(from), to: String(filterTo), bucket: bucketSize },
      skipRetention: true,
    });
    setLastFetchedDate(filterTo)
  };

  const createNoteByClick = async (event: any, chart: any) => {
    const x = event.offsetX;
    const y = event.offsetY;

    const dataCoords = chart.convertFromPixel({ seriesIndex: 0 }, [x, y]);

    const xValue = dataCoords[0];
    const yValue = dataCoords[1];

    if (linkingNoteIdRef.current) {
      const activeLinkingNoteId = linkingNoteIdRef.current;
      const coordinate = { x: xValue, y: yValue };

      if (currentUserMeasurement === CycleMeasurement.Fahrenheit) {
        coordinate.y = Number(fahrenheitToCelsius(coordinate.y));
      }

      const response = await createNoteLink({ noteId: activeLinkingNoteId, payload: { coordinate } });
      addLinkToListNotes(activeLinkingNoteId, response);

      const updatedListNotes = cycleRef.current.notes.map((noteData) =>
        noteData.id === activeLinkingNoteId
          ? { ...noteData, links: [...(noteData.links ?? []), response] }
          : noteData
      );
      updateCycleData({ notes: updatedListNotes });
      return;
    }

    formik.handleChange({
      target: {
        name: 'coordinate',
        value: { x: xValue, y: yValue },
      },
    });

    openModalCreation();
  };

  const expandListNotes = (note: Note) => {
    const formattedTime = new Date(note.coordinate.x);
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, note.coordinate.y);

    const newNote: NoteSeriesDataType = {
      name: generateNoteName(note.name),
      id: note.id,
      media: { coord: [formattedTime, convertedNoteCoordY] },
    };

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = [...oldValue.noteSeriesData, newNote].sort((a, b) => new Date(a.media.coord[0]).valueOf() - new Date(b.media.coord[0]).valueOf());

      const newSource = oldValue.source.map((item) => {
        if (item.time === note.coordinate.x) {
          return { ...item, [note.name]: convertedNoteCoordY };
        }

        return item;
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
        source: newSource,
      };
    });
  };

  const deleteNoteFromListNotes = (id: string) => {
    setCycleComponent((oldValue) => ({
      ...oldValue,
      noteSeriesData: oldValue.noteSeriesData.filter((note) => note.id !== id),
    }));
  };

  const addLinkToListNotes = (noteId: string, link: NoteLink) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, link.coordinate.y);
    const formattedTime = new Date(link.coordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          links: [...(note.links ?? []), { id: link.id, coord: [formattedTime, convertedNoteCoordY] as [Date, number] }],
        };
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
      };
    });
  };

  const removeLinkFromListNotes = (noteId: string, linkId: string) => {
    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          links: (note.links ?? []).filter((link) => link.id !== linkId),
        };
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
      };
    });
  };

  const promoteLinkInListNotes = (noteId: string, promotedLinkId: string, newCoordinate: { x: number; y: number }) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, newCoordinate.y);
    const formattedTime = new Date(newCoordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          media: { coord: [formattedTime, convertedNoteCoordY] },
          links: (note.links ?? []).filter((link) => link.id !== promotedLinkId),
        };
      });

      return {
        ...oldValue,
        noteSeriesData: sortNoteSeries(newNoteSeriesData),
      };
    });
  };

  const updateListNotes = (oldNote: Note, updatedNote: Note) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, updatedNote.coordinate.y);
    const formattedTime = new Date(updatedNote.coordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((item) => {
        if (item.id === updatedNote.id) {
          return {
            name: generateNoteName(updatedNote.name),
            id: updatedNote.id,
            media: { coord: [formattedTime, convertedNoteCoordY] },
            links: item.links,
          };
        }

        return item;
      });

      const newSource = oldValue.source.map((item) => {
        if (item.time === formattedTime.valueOf()) {
          const newSourceItem = { ...item };
          delete newSourceItem[oldNote.name];

          newSourceItem[updatedNote.name] = convertedNoteCoordY;

          return newSourceItem;
        }

        return item;
      });

      return {
        ...oldValue,
        noteSeriesData: sortNoteSeries(newNoteSeriesData),
        source: newSource,
      };
    });
  };

  const openModalEditing = (data: Note, occurrenceId: string = 'root') => {
    formik.setValues({
      ...data,
    });

    setEditingOccurrenceId(occurrenceId);
    setIsEditNote(true);
  };

  const closeModalEditing = () => {
    formik.resetForm();
    setIsEditNote(false);
    setIsDeletionNote(false);
    setEditingOccurrenceId('root');
    setIsDeletingChain(false);
  };

  const openModalChartPreferences = () => {
    setIsEditChartPreferences(true);
  };

  const openModalReportChannels = () => {
    setIsEditReportChannels(true)
  };

  const closeModalChartPreferences = (isChangedValues: boolean = false) => {
    setIsEditChartPreferences(false);

    if (isChangedValues) {
      setIsLoading(true);
      fetchData(id);
    }
  };

  const openModalCreation = () => {
    setIsCreationNote(true);
  };

  const closeModalCreation = () => {
    formik.resetForm();
    setIsCreationNote(false);
    setLinkTargetNoteId(null);
  };

  const showDeleteNote = () => {
    setIsDeletionNote(true);
    setIsDeletingChain(false);
  };

  const showDeleteChain = () => {
    setIsDeletionNote(true);
    setIsDeletingChain(true);
  };

  const closeDeleteNote = () => {
    setIsDeletionNote(false);
    setIsDeletingChain(false);
  };

  const deleteLinkOccurrence = async (noteId: string, linkId: string) => {
    await deleteNoteLink({ noteId, linkId });

    removeLinkFromListNotes(noteId, linkId);

    const updatedListNotes = cycleRef.current.notes.map((noteData) =>
      noteData.id === noteId
        ? { ...noteData, links: (noteData.links ?? []).filter((link) => link.id !== linkId) }
        : noteData
    );
    updateCycleData({ notes: updatedListNotes });

    closeModalEditing();
  };

  const editNoteByClick = (params) => {
    params.event.event.stopPropagation();

    if (linkingNoteIdRef.current) {
      return;
    }

    if (params.componentType === 'markPoint' && formik.values.id === null) {
      setCycleComponent((oldValue) => {
        const { noteSeriesData } = oldValue;
        const note = noteSeriesData.find((note) => params.seriesId.includes(note.id));

        if (!note) {
          return oldValue;
        }

        const { links: _links, ...noteRest } = note;
        const formNote: Note = { ...noteRest, coordinate: { x: note.media.coord[0].valueOf(), y: note.media.coord[1] } }
        const occurrenceId: string = params.data?.occurrenceId ?? 'root';
        openModalEditing(formNote, occurrenceId);

        return oldValue;
      });
    }
  };

  const linkNoteOptions: Option[] = useMemo(() => {
    if (!cycleComponents) return [];

    return cycleComponents.noteSeriesData.map((note, index) => ({
      id: note.id,
      value: `#${index + 1} ${note.name}`,
    }));
  }, [cycleComponents]);

  const selectedLinkOption: Option | null = useMemo(() => {
    if (!linkTargetNoteId) return null;

    return linkNoteOptions.find((option) => option.id === linkTargetNoteId) ?? null;
  }, [linkTargetNoteId, linkNoteOptions]);

  const linkTargetNote = useMemo(() => {
    return cycle?.notes?.find((note) => note.id === linkTargetNoteId) ?? null;
  }, [cycle, linkTargetNoteId]);

  const editingNoteHasLinks = useMemo(() => {
    return (cycleComponents?.noteSeriesData?.find((note) => note.id === formik.values.id)?.links?.length ?? 0) > 0;
  }, [cycleComponents, formik.values.id]);

  const selectLinkTarget = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value as unknown as SelectedValue;
    setLinkTargetNoteId(selectedValue.id === 'DEFAULT_OPTION' ? null : selectedValue.id);
  };

  const startLinkingMode = (noteId: string) => {
    setLinkingNoteId(noteId);
    closeModalEditing();
  };

  const stopLinkingMode = () => {
    setLinkingNoteId(null);
  };

  const changeViewNotes = () => {
    updateViewNotes({
      id: currentUserMember.id,
      is_show_notes: !currentUserMember.is_show_notes,
    });
  };

  const clearData = () => {
    clearCycle();
    clearChartPreference();
    clearNote();
    clearChannels();
    setFirstLoadComplete(false);
  };

  useEffect(() => {
    fetchData(id);
    return () => {
      clearData();
    };
  }, [id]);

  useEffect(() => {
    if (isStopPullingIotData || !isPullingIotData || !lastFetchedDate || !isFirstLoadComplete) return;
    const timeId = setTimeout(() => {
      fetchNewData(lastFetchedDate);
    }, INTERVAL_UPDATE_DATA);

    return () => {
      clearTimeout(timeId);
    };
  }, [isPullingIotData, isStopPullingIotData, lastFetchedDate, isFirstLoadComplete]);

  useEffect(() => {
    if (firstLoadComplete) {
      setIsFirstLoadComplete(true);
    }
  }, [firstLoadComplete]);

  useEffect(() => {
    if (cycleNotes && cycleIotData && currentUserMeasurement && chartPreference) {
      const cycleComponentData = generateCycleData(cycleNotes, cycleIotData as Record<string, IoTDataResponse[]>, currentUserMeasurement);

      const { minTemp, maxTemp } = cycleComponentData
      setIotData(cycleIotData);
      setCycleComponent(cycleComponentData);
      updateTempRange(minTemp, maxTemp);
    }
  }, [cycleNotes, cycleIotData, currentUserMeasurement]);

  useEffect(() => {
    if (cycleControllers) {
      const newCycleControllersName = generateControllersName(cycleControllers);

      getDeviceNames(cycleControllers);

      setCycleControllersName(newCycleControllersName);
    }
  }, [cycleControllers]);

  return (
    <>
      <div className="h-full w-full c-static-data">
        {(isLoading || !chartPreference || iotData === null) && (
          <div className="c-static-data__spinner flex h-full flex-auto items-center justify-center">
            <SpinnerLoader />
          </div>
        )}
        {!isLoading && chartPreference && iotData && (
          <>
            <div className="c-static-data__info grid items-center pb-6 grid-cols-[auto_auto_1fr_auto] gap-x-4">
              <label className="c-static-data__note-tumbler flex h-full flex-row items-center rounded-md border border-primary px-2 py-1.5">
                <input
                  type="checkbox"
                  className="c-static-data__note-tumbler-checkbox h-5 w-5 flex-auto"
                  onChange={changeViewNotes}
                  checked={currentUserMember.is_show_notes}
                />
                <span className="overflow-hidden text-ellipsis pl-2 text-sm">{t('note.commonText.showNoteText')}</span>
              </label>
              {IS_ACCESS.CYCLE.UPDATE && (
                <Tooltip hint={t('labels.editLineColors')} hintClassName='left-[50%] bottom-9'>
                  <InteractiveButton
                    onClick={openModalReportChannels}
                    customType="max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer min-w-fit"
                    style={{ padding: ' 0px 12px' }}
                  >
                    <IconMultipleLines stroke="inherit" iconClassName="text-inherit" size={ElementSize.MD} />
                  </InteractiveButton>
                </Tooltip>
              )}
              <div></div>
              {IS_ACCESS.CHART_PREFERENCES.UPDATE && (
                <Tooltip hint={t('labels.chartSettings')} hintClassName='left-[50%] bottom-9'>
                  <InteractiveButton
                    onClick={openModalChartPreferences}
                    customType="max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer min-w-fit"
                    style={{ padding: ' 0px 12px' }}
                  >
                    <IconGear stroke="inherit" iconClassName="text-inherit" size={ElementSize.XS} />
                  </InteractiveButton>
                </Tooltip>
              )}
            </div>

            {linkingNoteId && cycleComponents && (
              <div className="mb-4 flex items-center justify-between rounded-md border border-primary bg-primary/10 px-4 py-2">
                <span className="text-sm">
                  {t('note.commonText.addingLinkedNotesFor')} #{getNumberNoteSeries(linkingNoteId, cycleComponents.noteSeriesData)}
                </span>
                <InteractiveButton
                  onClick={stopLinkingMode}
                  text={t('note.commonText.stopAdding')}
                  size={ElementSize.XS}
                  type={ButtonType.PrimaryInverse}
                />
              </div>
            )}

            <div className="relative">
              {IS_ACCESS.CYCLE.UPDATE && excludedChannelNames && cycleControllers && cycle.cycle_reports && (
                <GraphScreenshot
                  clientId={clientId}
                  contractId={contractId}
                  screenId={screenId}
                  cycleId={id}
                  dataZoom={dataZoom}
                  chartRef={chartRef}
                  cycleComponents={cycleComponents}
                  excludedChannelNames={excludedChannelNames}
                  cycleControllers={cycleControllers}
                  cycleReports={cycle.cycle_reports}
                />
              )}
              {!isFirstLoadComplete && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50"></div>
              )}
            </div>
            <div className="h-[calc(100%_-_234px)] w-full sm:h-[calc(100%_-_114px)] relative">
              <StaticChart
                cycleControllersName={cycleControllersName}
                createNote={createNoteByClick}
                editNote={editNoteByClick}
                measurement={currentUserMeasurement}
                cycleComponents={cycleComponents}
                isNoteView={currentUserMember.is_show_notes}
                chartPreference={chartPreference}
                nameDevices={nameDevices}
                chartRef={chartRef}
                excludedChannelNames={excludedChannelNames}
                cycleControllers={cycleControllers}
                colorsDevices={colorsDevices}
                generateNoteName={generateNoteName}
              />
              {!isFirstLoadComplete && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
                  <SpinnerLoader />
                </div>
              )}
            </div>
            {IS_ACCESS.NOTE.CREATE && (
              <Modal
                modalTitle="Add note"
                isVisible={isCreationNote}
                onClose={closeModalCreation}
                showAcceptBtn={false}
                showCancelBtn={false}
              >
                <form className="c-static-data__modal flex h-full flex-col" onSubmit={formik.handleSubmit}>
                  {linkNoteOptions.length > 0 && (
                    <div className="pb-4">
                      <SelectOption
                        name="linkTargetNoteId"
                        title={t('note.commonText.linkToExistingNote')}
                        placeholder={t('note.commonText.linkToExistingNotePlaceholder')}
                        options={linkNoteOptions}
                        selectedOption={selectedLinkOption}
                        onChange={selectLinkTarget}
                      />
                    </div>
                  )}
                  {linkTargetNoteId ? (
                    <div className="c-static-data__modal-linked-note-preview">
                      <p className="pb-1.5 text-sm font-medium 2xl:pb-2.5 2xl:text-base">
                        {t('note.commonText.linkedNoteText')}
                      </p>
                      <p className="text-sm 2xl:text-base">{linkTargetNote?.name}</p>
                    </div>
                  ) : (
                    <TextAreaControl
                      label={t('note.commonText.noteLabel')}
                      name="name"
                      placeholder={t('note.commonText.enterNote')}
                      value={formik.values.name}
                      customOnChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      showError={formik.touched.name && formik.errors.name}
                      errorText={formik.errors.name}
                      className="h-full resize-y max-sm:min-h-full max-sm:resize-none sm:max-h-[200px] lg:max-h-[430px]"
                    />
                  )}
                  <div className="c-static-data__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={closeModalCreation}
                      text={t('buttons.cancel')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                    <InteractiveButton text={t('buttons.save')} size={ElementSize.XL} disabled={isDisabledNoteBtn} submit />
                  </div>
                </form>
              </Modal>
            )}
            <Modal
              modalTitle={`${t('note.commonText.editNote')} #${getNumberNoteSeries(formik.values.id, cycleComponents.noteSeriesData)}`}
              isVisible={isEditNote}
              onClose={closeModalEditing}
              showAcceptBtn={false}
              showCancelBtn={false}
            >
              <form className="c-static-data__modal flex h-full flex-col" onSubmit={formik.handleSubmit}>
                {!isDeletionNote && (
                  <TextAreaControl
                    label={t('note.commonText.noteLabel')}
                    name="name"
                    placeholder={t('note.commonText.enterNote')}
                    value={formik.values.name}
                    customOnChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    showError={formik.touched.name && formik.errors.name}
                    errorText={formik.errors.name}
                    disabled={!IS_ACCESS.NOTE.DELETE || !IS_ACCESS.NOTE.UPDATE}
                    className="h-full resize-y max-sm:min-h-full max-sm:resize-none sm:max-h-[200px] lg:max-h-[430px]"
                  />
                )}
                {isDeletionNote && IS_ACCESS.NOTE.DELETE && (
                  <>
                    <div className="relative flex min-h-[152px] items-center justify-center">
                      <p className="pb-2 text-center text-xl">
                        {isDeletingChain ? t('note.commonText.confirmDeleteNoteChain') : t('note.commonText.confirmDeleteNote')}
                      </p>
                    </div>
                    <div className="c-static-data__modal-actions flex w-full flex-row justify-between pt-4">
                      <InteractiveButton
                        onClick={closeDeleteNote}
                        text={t('buttons.cancel')}
                        size={ElementSize.XL}
                        type={ButtonType.PrimaryInverse}
                      />
                      <InteractiveButton text={t('buttons.delete')} size={ElementSize.XL} submit />
                    </div>
                  </>
                )}
                {!isDeletionNote && IS_ACCESS.NOTE.CREATE && (
                  <div className="c-static-data__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={() => startLinkingMode(formik.values.id)}
                      text={t('note.commonText.addLinkedNotes')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                  </div>
                )}
                {!isDeletionNote && editingNoteHasLinks && IS_ACCESS.NOTE.DELETE && (
                  <div className="c-static-data__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={showDeleteChain}
                      text={t('note.commonText.deleteAllInChain')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                  </div>
                )}
                {!isDeletionNote && IS_ACCESS.NOTE.UPDATE && (
                  <div className="c-static-data__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={editingOccurrenceId === 'root' ? showDeleteNote : () => deleteLinkOccurrence(formik.values.id, editingOccurrenceId)}
                      text={t('buttons.delete')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                    <InteractiveButton text={t('buttons.save')} size={ElementSize.XL} disabled={isDisabledNoteBtn} submit />
                  </div>
                )}
              </form>
            </Modal>
            {isEditChartPreferences && chartPreference && (
              <ChartPreferences
                cycleId={id}
                isVisible={isEditChartPreferences}
                onOpen={openModalChartPreferences}
                onClose={closeModalChartPreferences}
                chartPreference={chartPreference}
                measurement={currentUserMeasurement}
                currentTempRange={tempRange}
              />
            )}
            {isEditReportChannels && cycleControllers && (
              <ReportChannelsModal
                cycleId={id}
                onOpen={openModalReportChannels}
                onClose={() => setIsEditReportChannels(false)}
                cycleControllers={cycleControllers}
                excludedDevices={excludedChannelNames}
                fetchChartSettings={fetchChartSettings}
                channelNames={channelNames}
                colorsDevices={getColorsDevicesByChart(chartRef, colorsDevices, cycle)}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

const mapState = (state: RootState) => ({
  cycle: state.cycles.cycle,
  cycleControllers: state.cycles.cycle?.cycle_controllers,
  cycleIotData: state.cycles.cycle?.iotDataResponse,
  // cycleIotData: parsedata,
  cycleNotes: state.cycles.cycle?.notes,
  currentUserMeasurement: getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
  currentUserMember: state.members.currentUserMember,
  chartPreference: state.chart_preference.chartPreference,
  firstLoadComplete: state.cycles.firstLoadComplete,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getCycle: dispatch.cycles.getChartCycleData,
  createNote: dispatch.note.createNote,
  deleteNote: dispatch.note.deleteNote,
  updateNote: dispatch.note.updateNote,
  createNoteLink: dispatch.note.createNoteLink,
  deleteNoteLink: dispatch.note.deleteNoteLink,
  updateViewNotes: dispatch.members.updateViewNotes,
  getChartPreference: dispatch.chart_preference.getOneByCycleId,
  clearCycle: dispatch.cycles.resetOne,
  clearChartPreference: dispatch.cycles.resetOne,
  clearNote: dispatch.note.resetState,
  clearChannels: dispatch.channels.resetChannelState,
  getChannels: dispatch.channels.getChannels,
  getExcludeChannelNames: dispatch.cycles.getExcludeChannelNames,
  getColorDevices: dispatch.cycles.getColorDevices,
  updateCycleData: dispatch.cycles.setOneData,
  getCustomChannelNames: dispatch.reports.getCustomChannelNames,
  setFirstLoadComplete: dispatch.cycles.setFirstLoadComplete,
});

export const StaticData = connect<StateProps, DispatchProps>(mapState, mapDispatch)(CycleChart);
